/**
 * Booked slot event content renderer for the manual calendar.
 * Shows interview name (truncated) + time range with a hover tooltip.
 * Handles error state for >24hr duration.
 *
 * Ref: KInterviewScheduler/.../ManualBookedContent.tsx + CalEventContent.tsx
 */
import { Flex } from '@chakra-ui/react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kula-ai/components';
import {
  formatEventTime,
  getEventPadding,
  calcDurationMinutes,
  MAX_DURATION_MINUTES,
} from './eventHelpers';

interface BookedEventContentProps {
  interviewName?: string;
  start: string;
  end: string;
  timezone?: string;
}

export const BookedEventContent = ({
  interviewName,
  start,
  end,
  timezone,
}: BookedEventContentProps) => {
  const title = interviewName || 'Interview';
  const time = formatEventTime(start, end, timezone);
  const duration = calcDurationMinutes(start, end);
  const padding = getEventPadding(duration);
  // A live resize gesture can drag the event past 24h; release re-clamps
  // to 1440 (`snapManualDurationMinutes`). While it's over, paint it red
  // with a "Max 24 hour" label so the ceiling is visible mid-drag —
  // legacy `ManualBookedContent` parity.
  const isDurationExceeded = duration > MAX_DURATION_MINUTES;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              cursor: 'pointer',
              backgroundColor: isDurationExceeded
                ? 'var(--kula-colors-error-200)'
                : undefined,
            }}
          >
            <div style={{ flex: 1, padding, overflow: 'hidden' }}>
              <div
                style={{
                  fontSize: '12px',
                  fontWeight: 600,
                  lineHeight: '16px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  color: 'inherit',
                }}
              >
                {title}
              </div>
              <div
                style={{
                  fontSize: '12px',
                  fontWeight: 500,
                  lineHeight: '16px',
                  whiteSpace: 'nowrap',
                  color: 'inherit',
                }}
              >
                {time}
              </div>
            </div>
            {isDurationExceeded && (
              <div
                style={{
                  backgroundColor: 'var(--kula-colors-error-100)',
                  color: '#ffffff',
                  fontSize: '10px',
                  textAlign: 'center',
                  padding: '1px 2px',
                  whiteSpace: 'nowrap',
                }}
              >
                Max 24 hour
              </div>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" align="start">
          <Flex flexDir="column" gap="2px">
            <span>{title}</span>
            <span>{time}</span>
          </Flex>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
